// SPDX-License-Identifier: GPL-3.0-only
// Adapted from pinned OpenSaga doors.cpp, giztimers.cpp, cutscene.cpp and
// menus/screens/menu.cpp. Source evidence and remaining gaps: SCRIPT_PARITY.md.
import { parseLevelRuntimeConfig, parseFlowGraph, FlowGraphRuntime, resolveDoorGeometry, checkDoorCrossing, entryFromDoor, startFromSpline } from './scripts.mjs';

const key = value => String(value).toLowerCase();
const pointsFor = (scene, name) => scene?.splines?.get(key(name))?.points ?? null;

// areas.cpp:847: these are area TXT directives, not a GIZ section. Fresh
// message storage is zeroed; only explicitly declared outputs are registered.
export function parseAreaMessages(text) {
  if (typeof text !== 'string' || text.length > 1024 * 1024) throw new Error('Invalid area message text.');
  const messages = new Map();
  for (const raw of text.split(/\r?\n/)) {
    const tokens = [...raw.replace(/\/\/.*$/, '').matchAll(/"([^"]*)"|([^\s]+)/g)].map(m => m[1] ?? m[2]);
    if (tokens[0]?.toLowerCase() !== 'aimessage') continue;
    const outputs = Array(8).fill(0); let count = 0;
    for (let i = 2; i < tokens.length; i += 2) {
      const index = /^output([0-7])$/i.exec(tokens[i]); const value = Number(tokens[i + 1]);
      if (!index || !Number.isInteger(value)) throw new Error('Invalid original AIMessage output.');
      const slot = Number(index[1]); outputs[slot] = (value << 24) >> 24; count = Math.max(count, slot + 1);
    }
    messages.set(`msg_${key(tokens[1])}`, { value: 0, outputs: outputs.slice(0, count) });
  }
  return messages;
}

export function parseCutsceneDescriptor(text, bytes) {
  if (typeof text !== 'string' || text.length > 1024 * 1024) throw new Error('Invalid cutscene configuration.');
  const buffer = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  if (!ArrayBuffer.isView(buffer) || buffer.byteLength < 48) throw new Error('Missing original CU2 data.');
  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  const version = view.getUint32(0, true), durationFrames = view.getFloat32(8, true);
  if (version !== 0x207 || !Number.isFinite(durationFrames) || durationFrames <= 0) throw new Error('Unsupported original CU2 header.');
  let gotoLevel = null, fps = 30, unskippableInStory = false;
  for (const raw of text.split(/\r?\n/)) {
    const tokens = [...raw.replace(/\/\/.*$/, '').matchAll(/"([^"]*)"|([^\s]+)/g)].map(m => m[1] ?? m[2]);
    const command = tokens[0]?.toLowerCase();
    if (command === 'goto_level') gotoLevel = key(tokens[1]);
    if (command === 'fpsec') fps = Number(tokens[1]);
    if (command === 'unskippable_in_story') unskippableInStory = true;
  }
  if (!Number.isFinite(fps) || fps <= 0) throw new Error('Invalid source cutscene rate.');
  return Object.freeze({ version, durationFrames, fps, gotoLevel, unskippableInStory, rendererSupported: false });
}

export class MissionRuntime {
  #pending = null;
  #sequence = 0;
  #chain = null;
  #cutscene = null;
  constructor({ chapterId, mode = 'story', levelCatalog, worldBindings = {}, onTransition = () => {}, onTerminal = () => {}, onBlocked = () => {}, random = Math.random } = {}) {
    if (!chapterId || !['story', 'freeplay'].includes(mode) || !(levelCatalog?.levels instanceof Map) || !(levelCatalog?.areas instanceof Map)) throw new Error('Invalid mission catalog.');
    this.chapterId = key(chapterId); this.mode = mode; this.catalog = levelCatalog; this.world = worldBindings;
    this.onTransition = onTransition; this.onTerminal = onTerminal; this.onBlocked = onBlocked; this.random = random;
    const area = this.catalog.areas.get(this.chapterId);
    if (!area) throw new Error('Unknown mission chapter.');
    this.levelIds = new Set(area.levelIds); this.doors = new Map(); this.timers = new Map(); this.messages = new Map();
  }
  get pendingTransition() { return this.#pending; }
  get cutscene() { return this.#cutscene; }
  get statusLevelId() { return [...this.levelIds].find(id => this.catalog.levels.get(id)?.kind === 'status') ?? null; }
  enterLevel({ id, configText = '', gitText = '', scene = null, messages = new Map() }, { transition = null, playerIndex = 0, fallback = null } = {}) {
    id = key(id);
    if (!this.levelIds.has(id)) throw new Error('Level is outside this chapter.');
    if (transition && (transition !== this.#pending || transition.to !== id)) throw new Error('Unproven mission transition.');
    if (!transition) { this.#chain = null; this.#cutscene = null; this.#pending = null; }
    const config = parseLevelRuntimeConfig(configText), graph = gitText.trim() ? parseFlowGraph(gitText) : null;
    this.levelId = id; this.config = config; this.scene = scene; this.doors = new Map(); this.timers = new Map(); this.messages = new Map(messages);
    // batman.cpp fresh area initialization, ordinary (non-mission/challenge)
    // story/free-play modes supported by this runtime.
    for (const [name, value] of [['freeplay', this.mode === 'freeplay' ? 1 : 0], ['missionmode', 0], ['challengemode', 0]])
      this.messages.set(`msg_${name}`, { value, outputs: [1, 0] });
    for (const door of config.doors) {
      const points = pointsFor(scene, door.spline);
      let geometry = null;
      if (points) geometry = resolveDoorGeometry(door, points);
      else this.onBlocked({ reason: 'missing original door spline', level: id, door: door.name });
      this.doors.set(`door_${door.name}`, { door, enabled: true, geometry });
    }
    if (graph) for (const box of graph.boxes) for (const ref of box.gizmos ?? []) if (ref.type === 'giztimer' && !this.timers.has(ref.name)) this.timers.set(ref.name, { enabled: false, remaining: ref.timer ?? 0, duration: ref.timer ?? 0, random: ref.randomTime });
    const bindings = {
      activate: (ref, on) => this.activate(ref, on), setVisibility: (ref, on) => this.world.setVisibility?.(ref, on),
      getOutput: (ref, output, ignore) => this.getOutput(ref, output, ignore),
      activateReverse: (ref, on, invisible) => this.world.activateReverse?.(ref, on, invisible), actions: {
        setgizmovisibility: params => {
          let name = null, type = null, visible = true;
          for (const param of params) {
            if (/name=/i.test(param)) name = key(param.slice(param.toLowerCase().indexOf('name=') + 5));
            else if (/type=/i.test(param)) type = key(param.slice(param.toLowerCase().indexOf('type=') + 5));
            else if (/false/i.test(param)) visible = false;
          }
          if (!name || typeof this.world.setVisibility !== 'function') return false;
          return this.world.setVisibility({ name, type }, visible) !== false;
        },
        ...(this.world.actions ?? {}),
      },
    };
    this.flow = graph ? new FlowGraphRuntime(graph, bindings, { freeplay: this.mode === 'freeplay' }) : null;
    const matching = transition?.exitName ? config.doors.find(door => door.name === transition.exitName) : null;
    this.entry = matching && pointsFor(scene, matching.spline) ? entryFromDoor(matching, pointsFor(scene, matching.spline), playerIndex, fallback) : startFromSpline(pointsFor(scene, 'start') ?? [], playerIndex);
    if (transition) {
      this.#pending = null;
      if (transition.terminal && this.#chain && id === this.statusLevelId) {
        const evidence = Object.freeze({ chapterId: this.chapterId, mode: this.mode, levelId: id, event: 'original-outro-status', door: this.#chain.exitName, from: this.#chain.from, skippedCutscene: transition.kind === 'cutscene-skip' });
        this.#chain = null; this.#cutscene = null; this.onTerminal(evidence);
      }
    }
    return { entry: this.entry, flow: this.tick(0), cutsceneReferences: config.cutscenes };
  }
  activate(ref, on) {
    const door = ref.type === 'door' ? this.doors.get(ref.name) : null;
    if (door) { door.enabled = !!on; return true; }
    const timer = ref.type === 'giztimer' ? this.timers.get(ref.name) : null;
    if (timer) { const random = timer.random ? this.random() : 1; if (!Number.isFinite(random) || random < 0 || random > 1) throw new Error('Invalid random timer sample.'); timer.remaining = timer.duration * random; timer.enabled = !!on; return true; }
    return this.world.activate?.(ref, on);
  }
  getOutput(ref, output, ignoreActivation = false) {
    const door = ref.type === 'door' ? this.doors.get(ref.name) : null;
    if (door) return door.enabled;
    const timer = ref.type === 'giztimer' ? this.timers.get(ref.name) : null;
    if (timer) return timer.enabled && timer.remaining <= 0;
    if (ref.type === 'message') {
      const message = this.messages.get(ref.name);
      return message && Number.isInteger(output) && output >= 0 && output < message.outputs.length ? message.value === message.outputs[output] : null;
    }
    return this.world.getOutput?.(ref, output, ignoreActivation) ?? null;
  }
  tick(deltaSeconds) {
    if (!Number.isFinite(deltaSeconds) || deltaSeconds < 0) throw new Error('Invalid mission time.');
    for (const timer of this.timers.values()) if (timer.remaining >= 0) timer.remaining -= deltaSeconds;
    return this.flow?.tick() ?? { states: [], blocked: [] };
  }
  #request(to, extra) {
    if (this.#pending) return this.#pending;
    if (!this.levelIds.has(to)) { this.onBlocked({ reason: 'destination outside original chapter', level: to }); return null; }
    this.#pending = Object.freeze({ token: ++this.#sequence, from: this.levelId, to, ...extra });
    this.onTransition(this.#pending); return this.#pending;
  }
  updateActor({ previousCenter, currentCenter, playerActive = true, ignoreDoors = false, missionActive = false } = {}) {
    if (this.#pending || this.#cutscene) return null;
    for (const { door, enabled, geometry } of this.doors.values()) {
      if (!geometry) continue;
      const crossing = checkDoorCrossing(geometry, previousCenter, currentCenter, { story: this.mode === 'story', disabled: !enabled, playerActive, ignoreDoors, missionActive, currentLevel: this.levelId });
      if (!crossing) continue;
      if (door.cutscene) { this.onBlocked({ reason: 'door cinematic must finish before transition', door: door.name, cutscene: door.cutscene }); return null; }
      const target = this.catalog.levels.get(crossing.level);
      if (target?.kind === 'outro' || target?.kind === 'status') this.#chain = { from: this.levelId, exitName: door.name, outro: target.kind === 'outro' ? crossing.level : null };
      return this.#request(crossing.level, { ...crossing, terminal: target?.kind === 'status' && this.mode === 'freeplay' });
    }
    return null;
  }
  loadCutscene({ reference, configText, bytes }) {
    if (!this.#chain || this.#chain.outro !== this.levelId || !this.config.cutscenes.includes(key(reference))) throw new Error('Cutscene is not the reached original outro.');
    const descriptor = parseCutsceneDescriptor(configText, bytes);
    const target = descriptor.gotoLevel === 'map' ? this.statusLevelId : descriptor.gotoLevel;
    this.#cutscene = Object.freeze({ reference: key(reference), ...descriptor, target });
    return this.#cutscene;
  }
  skipCutscene({ userInitiated = false, replayViewer = false } = {}) {
    if (!userInitiated || replayViewer || !this.#cutscene || (this.mode === 'story' && this.#cutscene.unskippableInStory) || this.#cutscene.target !== this.statusLevelId) return null;
    return this.#request(this.statusLevelId, { kind: 'cutscene-skip', terminal: true, exitName: this.#chain.exitName, cutscene: this.#cutscene.reference });
  }
  // The actual CU2 player, once implemented, may call this on its finished
  // signal. An elapsed wall-clock timer is deliberately not such a signal.
  cutsceneFinished() {
    if (!this.#cutscene || this.#cutscene.target !== this.statusLevelId) return null;
    return this.#request(this.statusLevelId, { kind: 'cutscene-finished', terminal: true, exitName: this.#chain.exitName, cutscene: this.#cutscene.reference });
  }
  cancelTransition(token) {
    if (this.#pending?.token !== token) return false;
    if (!this.#pending.kind.startsWith('cutscene-')) this.#chain = null;
    this.#pending = null; return true;
  }
  commitTransition(token, destination, options = {}) {
    if (this.#pending?.token !== token) throw new Error('Stale mission transition.');
    return this.enterLevel(destination, { ...options, transition: this.#pending });
  }
}
